import api from './api';

// ─────────────────────────────────────────────────────────────────────────────
// Admin: Stories — Instagram-style cards управление в личном кабинете.
// Backend: admin-go (rabotamax-admin-api), /api/admin/stories/*
// ─────────────────────────────────────────────────────────────────────────────

export type StoryStatus = 'draft' | 'published' | 'archived';
export type TargetAudience = 'all' | 'free' | 'paid' | 'admin';
export type HeadlineFont = 'sans' | 'serif';

export interface StorySlide {
  id: number;
  story_id: number;
  position: number;
  duration_ms: number;
  eyebrow?: string | null;
  headline: string;
  body?: string | null;
  background_gradient: string;
  text_color: string;
  image_url?: string | null;
  headline_font: HeadlineFont;
  cta_label?: string | null;
  cta_url?: string | null;
  created_at: string;
  // Dynamic-system-stories: если iterate_over задан, resolver клонирует слайд
  // N раз (cap = max_iterations), подставляя {{item.X}} переменные.
  iterate_over?: string | null;
  max_iterations?: number;
}

export type StoryKind = 'static' | 'system';
export type RefreshStrategy = 'static' | 'daily' | 'every_24h' | 'every_open';

export interface Story {
  id: number;
  slug: string;
  title: string;
  cover_emoji?: string | null;
  cover_gradient: string;
  card_text_color: string;
  status: StoryStatus;
  display_order: number;
  target_audience: TargetAudience;
  published_at: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  slides?: StorySlide[];
  // Dynamic-system-stories поля.
  kind?: StoryKind;
  preset_id?: string | null;
  data_sources?: string[];
  refresh_strategy?: RefreshStrategy;
}

export interface StoryStats {
  story_id: number;
  slug?: string;
  title?: string;
  status?: StoryStatus;
  impressions: number;
  completions: number;
  avg_slides_viewed: number;
  cta_clicks: number;
}

export interface CreateStoryPayload {
  slug: string;
  title: string;
  cover_emoji?: string | null;
  cover_gradient: string;
  card_text_color: string;
  status?: StoryStatus;
  display_order?: number;
  target_audience?: TargetAudience;
  expires_at?: string | null;
  kind?: StoryKind;
  preset_id?: string | null;
  data_sources?: string[];
  refresh_strategy?: RefreshStrategy;
}

export interface UpdateStoryPayload {
  slug?: string;
  title?: string;
  cover_emoji?: string | null;
  cover_gradient?: string;
  card_text_color?: string;
  status?: StoryStatus;
  display_order?: number;
  target_audience?: TargetAudience;
  // Scheduling: published_at (отложенный публиш — story невидим до этой даты)
  // и expires_at (story исчезает после этой даты; для announcement-stories).
  published_at?: string | null;
  expires_at?: string | null;
  set_cover_emoji_null?: boolean;
  set_published_at_null?: boolean;
  set_expires_at_null?: boolean;
  kind?: StoryKind;
  preset_id?: string | null;
  set_preset_id_null?: boolean;
  data_sources?: string[];
  refresh_strategy?: RefreshStrategy;
}

export interface CreateSlidePayload {
  position?: number;
  duration_ms?: number;
  eyebrow?: string | null;
  headline: string;
  body?: string | null;
  background_gradient: string;
  text_color: string;
  image_url?: string | null;
  headline_font?: HeadlineFont;
  cta_label?: string | null;
  cta_url?: string | null;
  append_to_end?: boolean;
  iterate_over?: string | null;
  max_iterations?: number;
}

export interface UpdateSlidePayload {
  position?: number;
  duration_ms?: number;
  eyebrow?: string | null;
  headline?: string;
  body?: string | null;
  background_gradient?: string;
  text_color?: string;
  image_url?: string | null;
  headline_font?: HeadlineFont;
  cta_label?: string | null;
  cta_url?: string | null;
  set_eyebrow_null?: boolean;
  set_body_null?: boolean;
  set_image_url_null?: boolean;
  set_cta_label_null?: boolean;
  set_cta_url_null?: boolean;
  iterate_over?: string | null;
  set_iterate_over_null?: boolean;
  max_iterations?: number;
}

// API envelope: { success: boolean, story?, stories?, stats?, slide?, message? }

export interface StoryVariableDef {
  path: string;
  label: string;
  type: string;
  example: string;
}

export interface StorySourceDef {
  id: string;
  label: string;
  shape: 'scalar_object' | 'array';
  variables: StoryVariableDef[];
  itemVars?: StoryVariableDef[];
}

export interface StoryVariablesRegistry {
  sources: StorySourceDef[];
  iterableSources: string[];
  refreshStrategies: { value: RefreshStrategy; label: string }[];
  presets: { value: string; label: string }[];
}

export const storiesAdminService = {
  variables: async (): Promise<StoryVariablesRegistry> => {
    const r = await api.get('/admin/stories/variables');
    return r.data;
  },
  list: async (): Promise<Story[]> => {
    const r = await api.get('/admin/stories');
    return r.data?.stories || [];
  },
  get: async (id: number): Promise<Story> => {
    const r = await api.get(`/admin/stories/${id}`);
    return r.data?.story;
  },
  create: async (body: CreateStoryPayload): Promise<Story> => {
    const r = await api.post('/admin/stories', body);
    return r.data?.story;
  },
  update: async (id: number, body: UpdateStoryPayload): Promise<Story> => {
    const r = await api.patch(`/admin/stories/${id}`, body);
    return r.data?.story;
  },
  publish: async (id: number): Promise<Story> => {
    const r = await api.post(`/admin/stories/${id}/publish`, {});
    return r.data?.story;
  },
  archive: async (id: number): Promise<Story> => {
    const r = await api.post(`/admin/stories/${id}/archive`, {});
    return r.data?.story;
  },
  remove: async (id: number): Promise<void> => {
    await api.delete(`/admin/stories/${id}`);
  },
  reorder: async (ids: number[]): Promise<void> => {
    await api.post('/admin/stories/reorder', { ids });
  },

  // ── slides ──────────────────────────────────────────────────────────────
  addSlide: async (storyId: number, body: CreateSlidePayload): Promise<StorySlide> => {
    const payload = { append_to_end: true, ...body };
    const r = await api.post(`/admin/stories/${storyId}/slides`, payload);
    return r.data?.slide;
  },
  updateSlide: async (
    storyId: number,
    slideId: number,
    body: UpdateSlidePayload,
  ): Promise<StorySlide> => {
    const r = await api.patch(`/admin/stories/${storyId}/slides/${slideId}`, body);
    return r.data?.slide;
  },
  deleteSlide: async (storyId: number, slideId: number): Promise<void> => {
    await api.delete(`/admin/stories/${storyId}/slides/${slideId}`);
  },
  reorderSlides: async (storyId: number, ids: number[]): Promise<void> => {
    await api.post(`/admin/stories/${storyId}/slides/reorder`, { ids });
  },

  // ── stats ───────────────────────────────────────────────────────────────
  stats: async (id: number): Promise<StoryStats | null> => {
    try {
      const r = await api.get(`/admin/stories/${id}/stats`);
      return r.data?.stats || null;
    } catch (e) {
      return null;
    }
  },
  statsAll: async (): Promise<StoryStats[]> => {
    try {
      const r = await api.get('/admin/stories/stats');
      return r.data?.stats || [];
    } catch (e) {
      return [];
    }
  },
};

// ── Gradient presets ──────────────────────────────────────────────────────────
// 4 presets + custom. Used by both story cover and slide background.
export const GRADIENT_PRESETS: Array<{ id: string; label: string; value: string }> = [
  {
    id: 'sunset',
    label: 'Закат',
    value: 'linear-gradient(135deg, #FF6B6B 0%, #FFA45B 50%, #FFD66B 100%)',
  },
  {
    id: 'ocean',
    label: 'Океан',
    value: 'linear-gradient(135deg, #2E3192 0%, #1BFFFF 100%)',
  },
  {
    id: 'forest',
    label: 'Лес',
    value: 'linear-gradient(135deg, #134E5E 0%, #71B280 100%)',
  },
  {
    id: 'lavender',
    label: 'Лаванда',
    value: 'linear-gradient(135deg, #A18CD1 0%, #FBC2EB 100%)',
  },
];

export const DEFAULT_STORY: CreateStoryPayload = {
  slug: '',
  title: 'Новая история',
  cover_emoji: null,
  cover_gradient: GRADIENT_PRESETS[0].value,
  card_text_color: '#FFFFFF',
  status: 'draft',
  display_order: 0,
  target_audience: 'all',
};

export const DEFAULT_SLIDE: CreateSlidePayload = {
  position: 0,
  duration_ms: 5000,
  headline: 'Заголовок слайда',
  body: '',
  background_gradient: GRADIENT_PRESETS[1].value,
  text_color: '#FFFFFF',
  headline_font: 'sans',
  append_to_end: true,
};
